import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Card, Stack, Skeleton } from "@mui/material";
import Chart from "react-apexcharts";

const SignupTrendsChart = ({ signups = [], isLoading = false }) => {
  const [days, setDays] = useState(7);

  const chartData = useMemo(() => {
    const buildRangeData = (rangeDays) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dateBuckets = Array.from({ length: rangeDays }, (_, index) => {
        const date = new Date(today);
        date.setDate(today.getDate() - (rangeDays - 1 - index));

        return date;
      });

      const countsByDate = signups.reduce((acc, signup) => {
        const createdAt = signup?.createdAt ? new Date(signup.createdAt) : null;

        if (!createdAt || Number.isNaN(createdAt.getTime())) {
          return acc;
        }

        createdAt.setHours(0, 0, 0, 0);

        if (createdAt < dateBuckets[0] || createdAt > today) {
          return acc;
        }

        const key = createdAt.toISOString().split("T")[0];

        acc[key] = (acc[key] || 0) + 1;

        return acc;
      }, {});

      return {
        categories: dateBuckets.map((date) =>
          date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
        ),
        series: dateBuckets.map((date) => {
          const key = date.toISOString().split("T")[0];

          return countsByDate[key] || 0;
        }),
      };
    };

    return {
      7: buildRangeData(7),
      30: buildRangeData(30),
      90: buildRangeData(90),
    };
  }, [signups]);

  const current = chartData[days];

  const options = {
    chart: {
      id: "signup-trends",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "inherit",
      background: "transparent",
    },
    colors: ["#C4912E"],
    stroke: {
      curve: "smooth",
      width: 3,
    },
    fill: {
      type: "solid",
      opacity: 0.15,
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 5,
      colors: ["#fff"],
      strokeColors: "#C4912E",
      strokeWidth: 2,
      hover: { size: 7 },
    },
    grid: {
      borderColor: "#f1f1f1",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories: current.categories,
      labels: {
        style: { colors: "#666", fontSize: "12px" },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#666", fontSize: "12px" },
      },
    },
    tooltip: {
      theme: "light",
      y: { title: { formatter: () => "Signups:" } },
    },
  };

  return (
    <Card
      className="chart-section-card"
      sx={{
        p: 1,
        mb: 4,
        borderRadius: "8px",
        boxShadow: "none",
        border: "1px solid #EAEAEA",
      }}
    >
      <Stack
        direction="row"
        sx={{
          mb: 2,
          pb: 1,
          borderBottom: "1px solid #C4912E",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          className="chart-title"
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: "#0F1E3D",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📈 Daily Signup Trends
        </Typography>

        <Stack direction="row" spacing={1}>
          {[7, 30, 90].map((d) => (
            <Button
              key={d}
              className={`range-button-${d}`}
              size="small"
              variant={days === d ? "contained" : "outlined"}
              onClick={() => setDays(d)}
              sx={{
                textTransform: "none",
                borderRadius: "6px",
                fontWeight: 600,
                fontSize: { xs: "0.6rem", sm: "0.6125rem", md: "1rem" },
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 0.5 },
                minWidth: { xs: "60px", sm: "auto" },

                backgroundColor: days === d ? "#C4912E" : "transparent",
                color: days === d ? "#fff" : "#333",
                borderColor: days === d ? "#C4912E" : "#ddd",
                "&:hover": {
                  backgroundColor: days === d ? "#A67A26" : "#f5f5f5",
                  borderColor: "#C4912E",
                },
              }}
            >
              {d} Days
            </Button>
          ))}
        </Stack>
      </Stack>

      <Box
        className="apex-chart-wrapper"
        sx={{
          width: "100%",
          borderRadius: "4px",
          p: 1,
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            height={320}
            sx={{ borderRadius: "8px" }}
          />
        ) : (
          <Chart
            options={options}
            series={[{ name: "Signups", data: current.series }]}
            type="area"
            height={320}
          />
        )}
      </Box>
    </Card>
  );
};

export default SignupTrendsChart;
